import React, { useEffect, useRef, useState } from "react";
import { useReportAutoRun } from "hooks/useReportAutoRun/useReportAutoRun";
import { useLocation } from "react-router-dom";

import { delay, downloadCsv, formatDate, openPrintWindow } from "helpers/Helpers";

import { GetReport, GetReportStatus } from "api/rpc/2024-04/facilityAdmin/report/report";
import { StatusCode } from "api/protocols";
import { IConfiguration } from "api/rpc/clientAdmin/client/report";

import Page from "components/page/Page";
import Card from "components/card/Card";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { useTranslation } from "react-i18next";

import "../report.scss";
import { useAppDispatch } from "hooks/redux";
import { closeInfo, showError, showInfo, showSuccess } from "redux/actions/ui";
import moment from "moment";
import ReportEmailModal from "components/reportEmailModal/ReportEmailModal";
import { useReportRun } from "../useReportRun";
import LoyaltyProgramPointsBalanceTable from "../tables/LoyaltyProgramPointsBalanceTable";
import { GetActiveLoyaltyCustomerCount } from "api/rpc/2024-04/facilityAdmin/customer/loyaltyCustomer";
import ReactDOM from "react-dom";

interface IReportState {
  id: number;
  title: string;
  code: string;
  dateRangeStart: Date;
  singleDate: Date;
  configurations: Array<IConfiguration>;
  date: string;
  facilityOptions: Array<{ label: string; onClick: () => void }>;
  facility_id_name: string;
  email: string;
  queue_after_days: number;
  [key: string]: any;
}

interface IReportDataState {
  headings: any;
  rows: any;
  columns: Array<Record<string, any>>;
  tableDisplay: boolean;
  description: string;
  compileDate: string;
}

// Queue the report if the client has >= this many loyalty program customers
const LOYALTY_POINTS_BALANCE_QUEUE_THRESHOLD = 5000;

export default function LoyaltyProgramPointsBalance() {
  const GetReportRun = useReportRun();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const routeName = location.pathname.split("/");
  const code = routeName[routeName.length - 1];
  const _APIPollFlag = useRef(false);
  const [pollCount, setPollCount] = useState<number>(0);
  const [loyaltyCustomersCount, setLoyaltyCustomersCount] = useState<number>(null);

  const today = new Date();

  const [state, setState] = useState<IReportState>({
    dateRangeStart: today,
    dateRangeEnd: today,
    singleDate: today,
    id: null,
    title: "",
    code: "",
    configurations: [],
    date: formatDate(today),
    facilityOptions: [],
    facility_id_name: "",
    email: "",
    queue_after_days: 14,
  });

  const [reportData, setReportData] = useState<IReportDataState>({
    headings: [],
    rows: [],
    columns: [],
    tableDisplay: false,
    description: "",
    compileDate: "",
  });

  useEffect(() => {
    void loadReport();
  }, []);
  useReportAutoRun({
    isReady: state.id != null,
    reportId: state.id,
    setState,
    run: () => {
      void getReport(state.email);
    },
  });

  async function loadReport() {
    const [reportRes, countRes] = await Promise.all([GetReport({ code: code }, false), GetActiveLoyaltyCustomerCount(false)]);
    if (reportRes.status !== StatusCode.OK || countRes?.status !== StatusCode.OK) {
      return;
    }

    const report = reportRes.data[0];
    const sortedConfigurations = [...report.configurations].sort((prev, next) => prev.position - next.position);

    ReactDOM.unstable_batchedUpdates(() => {
      setLoyaltyCustomersCount(countRes?.data);
      setState(prev => ({
        ...prev,
        id: report.id,
        title: report.title,
        code: report.code,
        queue_after_days: report.queue_after_days,
        configurations: sortedConfigurations,
      }));
    });
  }

  function formatDateRange(startingDate: Date, endingDate: Date) {
    return [formatDate(startingDate), formatDate(endingDate) + " 23:59:59"];
  }

  function reportParams(email: string, type: string, should_queue: boolean) {
    return {
      report_id: state.id,
      as_of_date: formatDate(state?.dateRangeStart),
      email,
      should_queue,
      date_range: reportShouldQueue() && ( type === "csv" || type === "print") ? formatDateRange(state?.dateRangeStart, new Date(moment(state?.dateRangeStart)?.add(14, "days")?.toISOString())) : undefined,
      ...(type ? { type } : {}),
    };
  }

  const pollReport = async (executed_report_id: number) => {
    if (executed_report_id) {
      const res = await GetReportStatus({ executed_report_id }, false);
  
      if (res.status === StatusCode.OK && res.data?.status === "completed") {
        setPollCount(0);
        _APIPollFlag.current = false;
  
        const data = JSON.parse(res.data.report_data as string);
  
        setReportData(prev => ({
          ...prev,
          rows: data.rows,
          columns: data.columns,
          description: data?.description ?? "",
        }));
  
        dispatch(closeInfo());
      } else {
        if (_APIPollFlag.current === true) {
          setPollCount(prev => prev + 1);
  
          await delay(pollCount > 5 ? 5000 : 1000);
          await pollReport(executed_report_id);
        }
      }
    } else {
      _APIPollFlag.current = false;
    }
  };

  async function getReport(email: string) {
    setReportData(prev => ({
      ...prev,
      rows: [],
      columns: [],
      description: null,
    }));

    const shouldQueue = reportShouldQueue();

    if (shouldQueue) {
      dispatch(
        showInfo(
          "Loading report...",
          null,
          true,
          "Your report is being prepared. This may take a few minutes",
        ),
      );
    }
  
    const reportRes = await GetReportRun(reportParams(email, null, shouldQueue), !shouldQueue);

    if (reportRes.status !== StatusCode.OK) {
      dispatch(closeInfo());
      dispatch(showError(reportRes.message));
      return;
    }

    if (shouldQueue) {
      _APIPollFlag.current = true;
      void pollReport(reportRes.data?.executed_report_id as number);
    } else {
    const compileDate = reportRes.data?.compiled_at ? reportRes.data?.compiled_at.concat("Z") : "";

    setReportData(prev => ({
      ...prev,
      rows: reportRes.data.rows,
      columns: reportRes.data.columns,
      description: reportRes.data?.description ?? "",
      compileDate,
    }));
    }
    
  }

  async function getDownload(email: string) {
    const segmentsRes = await GetReportRun(reportParams(email, "csv", false), true);

    if (segmentsRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.tee_time_promotions.001")));
      return;
    }

    if (!email) {
      downloadCsv(segmentsRes.data as string, reportFilename() + ".csv");
    } else {
      dispatch(showSuccess(segmentsRes.message));
    }
  }

  async function getPrint(email: string) {
    const printRes = await GetReportRun(reportParams(email, "print", false), true);

    if (printRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.tee_time_promotions.002")));
      return;
    }

    if (!email) {
      openPrintWindow(printRes.data as string);
    } else {
      dispatch(showSuccess(printRes.message));
    }
  }

  function setDateRangeStart(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeStart: date,
    }));
  }

  const [reportEmail, setReportEmail] = useState({
    handler: null,
    open: false,
  });

  function handleReportEmailSelected(email: string) {
    setState({ ...state, email });
    setReportEmail({
      ...reportEmail,
      open: false,
    });
  }

  function handleReportEmailClose() {
    setReportEmail({ handler: null, open: false });
  }

  useEffect(() => {
    if (reportEmail.open === false && reportEmail.handler !== null) {
      reportEmail.handler(state.email);
    }
  }, [reportEmail.open]);

  const primaryAction = {
    content: t("Run"),
    action: () => {
        void getReport(state.email);
    },
  };

  function reportFilename(): string {
    return `${state.title}_${moment(new Date()).format("YYYY-MM-DD").toString()}`
      .replace(/ /g, "_")
      .toLowerCase();
  }

  function reportShouldQueue(): boolean {
    return loyaltyCustomersCount >= LOYALTY_POINTS_BALANCE_QUEUE_THRESHOLD;
  }

  function handleDownload() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getDownload,
      });
    } else {
      void getDownload("");
    }
  }

  function handlePrint() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getPrint,
      });
    } else {
      void getPrint("");
    }
  }

  return (
    <Page
      title={t("Loyalty Program Points Balance")}
      primaryAction={primaryAction}
      multipleActionDropdownAction={{
        label: t("Options"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("Export"),
              handler: handleDownload,
              icon: "file-arrow-down",
            },
            {
              type: "handler",
              label: t("Print"),
              handler: handlePrint,
              icon: "print",
            },
          ],
        },
      }}
      breadcrumbs={[
        { prefix: true, label: t("Back to Reports"), url: "/admin/report" },
      ]}
    >
      <Card>
        <Card.Section>
          <div className="report-filters-container">
            <DatePickerInput
              label={t("As Of Date")}
              months={1}
              position="left"
              startingDate={state.dateRangeStart}
              setStartingDate={setDateRangeStart}
              helpText={t("Shows the loyalty points balance for each customer as of the selected date")}
            />
          </div>
        </Card.Section>
      </Card>

      <div className="dynamic-table-container">
        <LoyaltyProgramPointsBalanceTable
          data={reportData.rows}
          columns={reportData.columns}
          description={reportData.description}
          compileDate={reportData.compileDate}
        />
      </div>
      <ReportEmailModal
        open={reportEmail.open}
        onEmailSelected={handleReportEmailSelected}
        onClose={handleReportEmailClose}
      />
    </Page>
  );
}
